import React, { useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { useDispatch } from 'react-redux';

import IconFemtasy from 'components/IconFemtasy';

import {
  Playlist,
  PlaylistTypeEnum,
  useGetStoryLovedLazyQuery,
  useToggleLoveStoryMutation
} from 'graphql/generated/graphql';

import { useAuth } from 'hooks/useAuth';
import useGraphaelDefaults from 'hooks/useGraphaelDefaults';
import useHandleAVSRedirect from 'hooks/useHandleAVSRedirect';
import { useTracking } from 'hooks/useTracking';

import { openAddStoryToPlaylist } from 'store/modules/playlist';
import './style.scss';

interface LoveProps {
  className?: string;
  femtasyRed?: boolean;
  itemId: string;
  loading: boolean;
  loveSvg?: React.ReactNode;
  loved?: boolean;
  lovedSvg?: React.ReactNode;
  matomoName?: string;
  matomoPiece?: string;
  playlists?: Pick<Playlist, 'id' | 'type'>[];
  toggleLoveStory: Function;
}

type LoveIconProps = Pick<
  LoveProps,
  | 'className'
  | 'femtasyRed'
  | 'itemId'
  | 'loveSvg'
  | 'loved'
  | 'lovedSvg'
  | 'matomoName'
  | 'matomoPiece'
  | 'playlists'
>;

const Love = ({
  itemId,
  loved,
  playlists,
  matomoName,
  matomoPiece,
  loading,
  loveSvg,
  lovedSvg,
}: LoveProps & {
  loved: boolean;
  playlists: Pick<Playlist, 'id' | 'type'>[];
}) => {
  const { trackContentInteraction } = useTracking();

  const isInUserPlaylists = playlists.some(
    (playlist) => playlist.type === PlaylistTypeEnum.User,
  );

  const [isLoved, setIsLoved] = useState(loved || isInUserPlaylists);
  const dispatch = useDispatch();

  React.useEffect(() => {
    setIsLoved(loved || isInUserPlaylists);
  }, [loved, isInUserPlaylists]);

  const doToggle = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (!loading) {
        dispatch(
          openAddStoryToPlaylist({
            id: itemId,
            loved,
            playlists,
          }),
        );
      }

      if (matomoName) {
        trackContentInteraction(
          loved ? 'unfollow' : 'follow',
          matomoName,
          matomoPiece || itemId,
        );
      }
    },
    [
      dispatch,
      itemId,
      loading,
      loved,
      matomoName,
      matomoPiece,
      playlists,
      trackContentInteraction,
    ],
  );

  return (
    <div onClick={doToggle} className={isLoved ? 'loved' : ''}>
      {isLoved
        ? lovedSvg || <IconFemtasy icon="Heartsolid" />
        : loveSvg || <IconFemtasy icon="Heartline" />}
    </div>
  );
};

const LoveIcon = ({
  femtasyRed = false,
  itemId,
  loved,
  playlists,
  className,
  ...props
}: LoveIconProps) => {
  const { isAuthenticated } = useAuth();
  const { handleAVSRedirect, userIsInAVSFlow } =
    useHandleAVSRedirect(femtasyRed);

  const [getStoryLoved, { data }] = useGetStoryLovedLazyQuery({
    ...useGraphaelDefaults(),
  });
  const [toggleLoveStory, { loading }] = useToggleLoveStoryMutation({
    ...useGraphaelDefaults(),
    variables: {
      storyId: itemId.toString(),
    },
    optimisticResponse: {
      toggleLoveStory: {
        __typename: 'Story',
        id: itemId.toString(),
        loved: loved === undefined ? !data?.story?.loved : !loved,
      },
    },
  });

  useEffect(() => {
    if ((loved === undefined || playlists === undefined) && !data) {
      getStoryLoved({ variables: { storyId: itemId.toString() } });
    }
  }, [loved, data, getStoryLoved, itemId, playlists]);

  if (!isAuthenticated || !itemId) {
    return null;
  }

  if (userIsInAVSFlow) {
    return <IconFemtasy icon="Lockline" onClick={() => handleAVSRedirect()} />;
  }

  return (
    <div className={cn('LoveIcon', className)}>
      <Love
        itemId={itemId.toString()}
        toggleLoveStory={toggleLoveStory}
        loved={loved || data?.story?.loved || false}
        playlists={playlists || data?.story?.playlists || []}
        loading={loading}
        {...props}
      />
    </div>
  );
};

export default LoveIcon;
